import { useEffect, useRef } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/Button";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const SearchExpand = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const SearchInput = styled.input`
  width: 180px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.text};
  background-color: ${colors.background};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`;

interface ReceiptsSearchBarProps {
  isExpanded: boolean;
  searchNumber: string;
  onSearchNumberChange: (value: string) => void;
  onSearch: () => void;
  isSearching: boolean;
}

export const ReceiptsSearchBar = ({
  isExpanded,
  searchNumber,
  onSearchNumberChange,
  onSearch,
  isSearching,
}: ReceiptsSearchBarProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  if (!isExpanded) {
    return null;
  }

  return (
    <SearchExpand>
      <SearchInput
        ref={inputRef}
        type="number"
        min={1}
        inputMode="numeric"
        value={searchNumber}
        onChange={(e) => onSearchNumberChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
        placeholder={t("receiptsView.searchPlaceholder")}
        aria-label={t("receiptsView.searchByNumber")}
      />
      <Button type="button" $small onClick={onSearch} disabled={isSearching}>
        {t("common.search")}
      </Button>
    </SearchExpand>
  );
};
